import { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import i18n from 'i18next';
import { useTranslation } from 'src/pages/authentication/i18n';
import { useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useGeneralError from 'src/hooks/useGeneralError';
import { useAuth } from 'src/contexts/JWTContext';
import { useSettings } from 'src/contexts/SettingsContext';
import {
  Box,
  Link,
  Typography,
  Button,
  Container,
  Stack,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from 'material-hu';
import { Helmet } from 'react-helmet-async';
import {
  AuthLayout,
  AuthCard,
  AzureLogin,
  OktaLogin,
  GoogleLogin,
  SelectInstance,
} from 'src/components/authentication';
import FormTextField from 'src/components/FormInputs/FormTextField';
import AcceptCancelDialog from 'src/components/AcceptCancelDialog';
import NewCommunityConfirmation from 'src/pages/authentication/components/NewCommunityConfirmation';
import TermsOfUseAndPrivacy from 'src/pages/authentication/components/TermsOfUseAndPrivacy';
import { PasswordInput } from 'src/components/authentication/PasswordInput';
import { formatTitle } from 'src/utils/helmetUtils';
import { validateRequired } from 'src/utils/validation';
import {
  getResetPasswordErrorMessage,
  getMessageToRecoverPassword,
} from 'src/utils/login';
import { ErrorCode } from 'src/constants/exceptions';
import {
  resetPassword,
  getInstances,
  getOTP,
} from 'src/services/authService';
import * as usersService from 'src/services/users';
import { LoginInstance } from 'src/types/login';
import { loginRoutes } from './routes';
import { loginKeys } from './queries';

const Login = () => {
  const [email, setEmail] = useState(''); // needed because react-hook-form doesnt submit disabled input's values
  const [instances, setInstances] = useState<LoginInstance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<LoginInstance | null>(null);
  const [emailSentModal, setEmailSentModal] = useState(false);
  const [confirmRecoverModal, setConfirmRecoverModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openNewCommunityConfirmation, setOpenNewCommunityConfirmation] = useState(false);

  const { t } = useTranslation();
  const { login, loginSaml } = useAuth();
  const { setPrimary, resetPrimary } = useSettings();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const showGeneralError = useGeneralError();
  const [searchParams] = useSearchParams();

  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const token = searchParams.get('bearerToken');

  useEffect(() => {
    (async () => {
      if (token) {
        const response = await loginSaml(token);
        const { instance } = response.data;
        setPrimary(instance?.color);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!selectedInstance) {
      resetPrimary();
    }
    else {
      setPrimary(selectedInstance?.color);
    }
  }, [selectedInstance, setPrimary, resetPrimary]);

  useEffect(() => {
    if (window.location.hash === '#instances') {
      if (!hasInstances || selectedInstance) {
        navigate('');
      }
    }
  }, [window.location.hash]);

  const hasInstances = instances.length > 0;

  const form = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setError,
  } = form;

  const setWrongCredentials = (field) => {
    setError(
      field,
      {
        type: 'manual',
        message: t('WRONG_CREDENTIALS'),
      },
    );
  };

  const handleCancelSelectInstance = () => {
    navigate('');
    setInstances([]);
  };

  const handleSelectInstance = (instance, username = email) => {
    const { id, samlURI, forceOTP } = instance;

    if (samlURI) {
      window.location.replace(samlURI);
    }
    if (forceOTP) {
      setPrimary(instance?.color);

      queryClient.prefetchQuery(
        loginKeys.otp.detail(id, username),
        () => getOTP(id, username),
      );

      navigate(loginRoutes.otp(),
        {
          state: {
            instance,
            employeeInternalId: username,
          },
        },
      );

      return;
    }

    navigate('');
    setSelectedInstance(instance);
  };

  const handleGetInstances = async (values) => {
    const response = await getInstances(values.email);
    if (response.data?.length) {
      setInstances(response.data);
      setEmail(values.email);

      if (response.data.length === 1) {
        handleSelectInstance(response.data[0], values.email);
      }
      else {
        navigate('#instances');
      }
    }
    else {
      setWrongCredentials('email');
    }
  };

  const changeLanguage = async (user, token) => {
    const userLanguage = navigator.language;
    const languageSymbol = userLanguage.substring(0, 2);
    const headers = { headers: { Authorization: `Bearer ${token}` } };
    await usersService.changeLanguageUser(user.id, languageSymbol, headers);
    i18n.changeLanguage(languageSymbol);
  };

  const handleLogin = async (values) => {
    const body = {
      employeeInternalId: values.email || email,
      instanceId: selectedInstance.id,
      password: values.password,
    };

    const { data } = await login(body);

    if (!data.user?.hasBeenLogged) {
      changeLanguage(data.user, data.token);
    }

    if (!data.user?.hasPasswordChanged) {
      navigate(loginRoutes.updatePassword(),
        {
          state: {
            token: data.token,
            id: data.user.id,
            loginData: {
              instanceId: data.instance.id,
              employeeInternalId: values.email || email,
            },
          },
        },
      );
    }
  };

  const submit = async (values) => {
    setLoading(true);
    try {
      !selectedInstance
        ? await handleGetInstances(values)
        : await handleLogin(values);
    }
    catch (err) {
      if (err.message === ErrorCode.NOT_ENOUGH_PERMISSIONS) {
        showGeneralError(err, t('NOT_ENOUGH_PERMISSIONS'));
      }
      else {
        setWrongCredentials(hasInstances ? 'password' : 'email');
      }
    }
    finally {
      setLoading(false);
    }
  };

  const openEmailSentModal = () => {
    setEmailSentModal(true);
  };

  const closeEmailSentModal = () => {
    setEmailSentModal(false);
  };

  const closeConfirmRecoverModal = () => {
    setConfirmRecoverModal(false);
  };

  const handleRecoverConfirmation = async () => {
    closeConfirmRecoverModal();
    const { id } = selectedInstance;
    setLoading(true);
    try {
      await resetPassword(email, id);
      openEmailSentModal();
    }
    catch (err) {
      showGeneralError(err, getResetPasswordErrorMessage(err.response.data.code));
    }
    finally {
      setLoading(false);
    }
  };

  const handleRecoverPasswordOnClick = () => {
    setConfirmRecoverModal(true);
  };

  const commonButtonProps = {
    fullWidth: true,
    disabled: isSubmitting || loading,
    size: isSmUp ? 'large' : 'medium' as any,
  };

  if (token) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Helmet>
        <title>{formatTitle(t('HEADER_TITLE'))}</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
        }}
      >
        <Container
          maxWidth="xs"
          sx={{ minHeight: '600px' }}
        >
          <AuthLayout>
            <AuthCard
              title={t('LOG_IN')}
              isLoading={isSubmitting || loading}
              titleVariant="h6"
            >
              <FormProvider {...form}>
                <form
                  noValidate
                  onSubmit={handleSubmit(submit)}
                >
                  <Stack
                    gap={1}
                    sx={{ alignItems: 'center' }}
                  >
                    {selectedInstance && (
                      <Stack sx={{ alignItems: 'center', mb: 3 }}>
                        <Box
                          component="img"
                          alt=""
                          src={selectedInstance.logo}
                          sx={{ maxWidth: '40%' }}
                        />
                        <Typography
                          color="textSecondary"
                          variant="body2"
                        >
                          {selectedInstance.name}
                        </Typography>
                      </Stack>
                    )}
                    <FormTextField
                      name="email"
                      rules={validateRequired()}
                      label={t('USERNAME_LABEL')}
                      autoFocus
                      showErrors
                      type="email"
                      variant="standard"
                      disabled={!!selectedInstance || loading}
                    />
                    {selectedInstance &&
                      selectedInstance.samlURI && (
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgress />
                        </Box>
                      )}
                    {selectedInstance &&
                      !selectedInstance.samlURI && (
                        <>
                          <PasswordInput
                            name="password"
                            label={t('PASSWORD_LABEL')}
                            variant="standard"
                            autoFocus
                            showErrors
                            rules={validateRequired()}
                            disabled={isSubmitting}
                          />
                          <Link
                            component="button"
                            type="button"
                            onClick={handleRecoverPasswordOnClick}
                            disabled={loading}
                          >
                            {t('FORGOT_PASSWORD')}
                          </Link>
                        </>
                      )}
                    <Button
                      {...commonButtonProps}
                      type="submit"
                      variant="contained"
                      sx={{
                        mt: 2,
                        fontSize: '14px',
                        fontWeight: '600',
                      }}
                    >
                      {t('CONTINUE')}
                    </Button>
                    {!selectedInstance && (
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center', my: 2 }}>
                          <Typography color="secondary">
                            {t('OR_LOGIN_WITH')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-evenly', width: '100%' }}>
                          <AzureLogin
                            {...commonButtonProps}
                            sx={{ mt: 2 }}
                            onLogin={() => setLoading(true)}
                            onLoginFinally={() => setLoading(false)}
                          />
                          <OktaLogin
                            {...commonButtonProps}
                            sx={{ mt: 2 }}
                            onLogin={() => setLoading(true)}
                            onLoginFinally={() => setLoading(false)}
                          />
                          <GoogleLogin
                            {...commonButtonProps}
                            sx={{ mt: 2 }}
                            onLogin={() => setLoading(true)}
                            onLoginFinally={() => setLoading(false)}
                          />
                        </Box>
                      </Box>
                    )}
                    {selectedInstance && instances.length > 1 && (
                      <Button
                        {...commonButtonProps}
                        variant="outlined"
                        onClick={() => setSelectedInstance(null)}
                      >
                        {t('SELECT_ANOTHER_COMMUNITY')}
                      </Button>
                    )}
                  </Stack>
                </form>
              </FormProvider>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Button
                  {...commonButtonProps}
                  variant="text"
                  sx={{ mt: 4, fontWeight: 400 }}
                  onClick={() => setOpenNewCommunityConfirmation(true)}
                >
                  {t('NEW_COMMUNITY_BUTTON')}
                </Button>
              </Box>
            </AuthCard>
            <TermsOfUseAndPrivacy />
          </AuthLayout>
        </Container>
      </Box>
      <SelectInstance
        open={hasInstances && !selectedInstance && window.location.hash === '#instances'}
        onSelect={handleSelectInstance}
        onCancel={handleCancelSelectInstance}
        instances={instances}
      />
      <NewCommunityConfirmation
        open={openNewCommunityConfirmation}
        onCancel={() => setOpenNewCommunityConfirmation(false)}
      />
      <AcceptCancelDialog
        title={t('GENERAL:CONFIRMATION')}
        open={confirmRecoverModal}
        onCancel={closeConfirmRecoverModal}
        onAccept={handleRecoverConfirmation}
        showCancel
      >
        <Typography>
          {t('CONFIRMATION_MESSAGE')}
        </Typography>
      </AcceptCancelDialog>
      <AcceptCancelDialog
        title={t('EMAIL_SENT')}
        open={emailSentModal}
        onAccept={closeEmailSentModal}
      >
        <Typography>
          {getMessageToRecoverPassword(selectedInstance?.userHasEmail)}
        </Typography>
      </AcceptCancelDialog>
    </Box>
  );
};

export default Login;
